import {
  AppBar,
  Box,
  IconButton,
  makeStyles,
  Toolbar,
  Theme
} from '@material-ui/core';
import { Home } from '@material-ui/icons';
import { isEmpty } from 'lodash';
import { useObserver } from 'mobx-react-lite';
import React, { FC, Fragment } from 'react';
import { isNumber, isNullOrUndefined } from 'util';
import { useOrdering } from '../../stores/OrderingStore';
import ViewStateContainer from '../Core/ViewStateContainer/ViewStateContainer';
import CartButton from './CartButton/CartButton';
import Ordering from './Ordering';
import { LayoutVariant } from '../Core/CommonTypes';
import InfoButton from './InfoDialog/InfoButton';
import { OnlineOrderingStore } from 'kiolyn-api';
import PromotionList from './PromotionList/PromotionList';
import AuthSection from '../Auth/AuthSection';
import Logo from './Logo';

const useStyles = makeStyles((theme: Theme) => {
  const tbHeight = isNumber(theme.mixins.toolbar.minHeight)
    ? theme.mixins.toolbar.minHeight
    : 0;

  return {
    homeButton: {},
    mainContent: {
      overflowY: 'auto',
      overflowX: 'hidden',
      height: `calc(100% - ${tbHeight + theme.spacing(3)}px)`,
      marginTop: theme.spacing(2)
    },
    mobileMainContent: {
      overflowY: 'auto',
      overflowX: 'hidden',
      height: `calc(100% - ${tbHeight + theme.spacing(2)}px)`,
      marginTop: theme.spacing(1)
    },
    menuTitle: {
      flexGrow: 1,
      lineHeight: 1.2,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      marginLeft: theme.spacing(2)
    },
    toolbar: {
      textAlign: 'center',
      position: 'relative'
    },
    blockLeft: {
      position: 'absolute',
      left: 0,
      marginLeft: 10
    },
    blockRight: {
      position: 'absolute',
      right: 0
    },
    logo: {
      margin: 'auto',
      marginTop: 5,
      marginBottom: 5
    }
  };
});

interface OrderingPageProps {
  variant?: LayoutVariant;
  homeURL?: string;
  onSubmit?: () => void;
  showOrderDetail?: boolean;
  setShowOrderDetail?: (show: boolean) => void;
  showInfoButton?: boolean;
  showLoginButton?: boolean;
  showInfoOrder?: boolean;
  setShowInfoButton?: (show: boolean) => void;
  setShowLoginButton?: (showLogin: boolean) => void;
  setShowInfoOrderDialog?: (show: boolean) => void;
  defaultView?: boolean;
}

const OrderingPage: FC<OrderingPageProps> = ({
  variant,
  homeURL,
  onSubmit,
  showOrderDetail,
  setShowOrderDetail,
  showInfoButton,
  showLoginButton,
  showInfoOrder,
  setShowInfoButton,
  setShowLoginButton,
  setShowInfoOrderDialog,
  defaultView
}: OrderingPageProps) => {
  const orderingStore = useOrdering();

  // const user = orderingStore.onlineUser as OnlineUser;

  const setShowOrderDetailDialog = (show: boolean) => {
    if (!isNullOrUndefined(setShowOrderDetail)) {
      setShowOrderDetail(show);
    }
  };

  const setShowInfoDialog = (show: boolean) => {
    if (!isNullOrUndefined(setShowInfoButton)) {
      setShowInfoButton(show);
    }
  };

  const setShowLoginDialog = (showLogin: boolean) => {
    if (!isNullOrUndefined(setShowLoginButton)) {
      setShowLoginButton(showLogin);
    }
  };

  const setShowInfoOrder = (show: boolean) => {
    if (!isNullOrUndefined(setShowInfoOrderDialog)) {
      setShowInfoOrderDialog(show);
    }
  };

  /// Reload the top level page to given URL
  const goHome = () => {
    if (isNullOrUndefined(homeURL)) {
      return;
    }
    window.top.location.href = homeURL;
  };

  const classes = useStyles();

  const store = orderingStore.store as OnlineOrderingStore;
  return useObserver(() => (
    <Fragment>
      <AppBar key="appBar" position="sticky">
        <Toolbar className={classes.toolbar}>
          <div className={classes.blockLeft}>
            {store.info_page_settings?.enable_info_page &&
              variant === LayoutVariant.mobile && (
                <InfoButton
                  name={orderingStore.order.name}
                  onClick={() => setShowInfoDialog(true)}
                />
              )}
            {!isEmpty(homeURL) && (
              <IconButton
                edge="start"
                className={classes.homeButton}
                color="inherit"
                aria-label="go home"
                onClick={goHome}
              >
                <Home />
              </IconButton>
            )}
            {store.info_page_settings?.enable_info_page &&
              (variant === LayoutVariant.laptop ||
                variant === LayoutVariant.kiosk) && (
                <InfoButton
                  name={orderingStore.order.name}
                  onClick={() => setShowInfoDialog(true)}
                />
              )}
            {orderingStore.promotions && !isEmpty(orderingStore.promotions) && (
              <PromotionList
                variant={variant}
                promotions={orderingStore.promotions}
              />
            )}
          </div>
          <Logo
            variant="h6"
            className={classes.logo}
            textClassName={classes.menuTitle}
            disableLogo
          />
          <div className={classes.blockRight}>
            {variant === LayoutVariant.mobile &&
              orderingStore.store.accept_order && (
                <CartButton
                  amount={orderingStore.order.total}
                  onClick={() => setShowOrderDetailDialog(true)}
                />
              )}
            {(variant === LayoutVariant.laptop ||
              variant === LayoutVariant.kiosk) &&
            store.is_show_auth ? (
              <AuthSection
                isButtonSignInText
                name={orderingStore.order.name}
                onClick={() => setShowLoginDialog(true)}
              />
            ) : null}
          </div>
        </Toolbar>
      </AppBar>
      <Box
        key="content"
        className={
          variant === LayoutVariant.mobile
            ? classes.mobileMainContent
            : classes.mainContent
        }
      >
        <ViewStateContainer
          key="content"
          state={orderingStore.viewState}
          onReload={() => orderingStore.reload()}
        >
          <Ordering
            orderingStore={orderingStore}
            acceptOrdering={orderingStore.store.accept_order}
            defaultView={defaultView}
            showOrderDetailDialog={showOrderDetail || false}
            showInfoDialog={showInfoButton || false}
            showLoginDialog={showLoginButton || false}
            showInfoOrderDialog={showInfoOrder || false}
            onClose={() => {
              setShowOrderDetailDialog(false);
              setShowInfoOrder(false);
              setShowInfoDialog(false);
              setShowLoginDialog(false);
            }}
            onClick={() => {
              setShowInfoDialog(false);
              setShowInfoOrder(true);
            }}
            onSubmit={onSubmit}
            variant={variant}
          />
        </ViewStateContainer>
      </Box>
    </Fragment>
  ));
};

export default OrderingPage;
